import { useState, useMemo, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import Fuse from 'fuse.js';
import { useVault } from '@/lib/supabase/VaultContext';
import { ItemCard } from '@/components/vault/ItemCard';
import { ItemDetailDrawer } from '@/components/vault/ItemDetailDrawer';
import { ItemCreateModal } from '@/components/vault/ItemCreateModal';
import { Button } from '@/components/ui/Button';
import { staggerContainer, staggerItem } from '@/lib/motion/presets';
import type { Item, ItemType } from '@/lib/supabase/types';
import {
  Search,
  Grid,
  List,
  Plus,
  Star,
  Tag as TagIcon,
  HardDrive,
  KeyRound,
  FileText,
  CreditCard,
  UserSquare,
  Binary,
  Compass,
  CornerDownRight,
} from 'lucide-react';

// Route param to database item_type mapping
const ROUTE_TO_TYPE: Record<string, ItemType> = {
  files: 'file',
  photos: 'photo',
  notes: 'note',
  passwords: 'password',
  cards: 'card',
  ids: 'id',
  keys: 'software_key',
};

const TYPE_SINGULARS: Record<ItemType, string> = {
  file: 'File',
  photo: 'Photo',
  note: 'Secure Note',
  password: 'Password',
  card: 'Credit Card',
  id: 'ID Document',
  software_key: 'Software Key',
};

export function VaultHome() {
  const { type } = useParams<{ type?: string }>();
  const { items, tags, loading } = useVault();

  // Filters state
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTagId, setSelectedTagId] = useState<string | null>(null);
  const [favoritesOnly, setFavoritesOnly] = useState(false);
  
  // Layout state
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  
  // Drawer/Modal state
  const [activeDetailItem, setActiveDetailItem] = useState<Item | null>(null);
  const [isCreateOpen, setIsCreateOpen] = useState(false);

  const searchInputRef = useRef<HTMLInputElement>(null);

  // Keyboard shortcut: Press '/' to focus search, Esc to clear
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === '/' && document.activeElement !== searchInputRef.current) {
        e.preventDefault();
        searchInputRef.current?.focus();
      } else if (e.key === 'Escape' && document.activeElement === searchInputRef.current) {
        setSearchQuery('');
        searchInputRef.current?.blur();
      }
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Filter type based on route
  const currentCategoryType = useMemo(() => {
    if (!type) return null;
    return ROUTE_TO_TYPE[type] || null;
  }, [type]);

  // Reset tag & favorite filters when category changes
  useEffect(() => {
    setSelectedTagId(null);
    setFavoritesOnly(false);
  }, [currentCategoryType]);

  // Filter items
  const filteredItems = useMemo(() => {
    let result = [...items];

    // 1. Category type filter
    if (currentCategoryType) {
      result = result.filter((it) => it.item_type === currentCategoryType);
    }

    // 2. Favorites filter
    if (favoritesOnly) {
      result = result.filter((it) => it.favorite);
    }

    // 3. Tag filter
    if (selectedTagId) {
      result = result.filter((it) => it.tags?.some((t) => t.id === selectedTagId));
    }

    // 4. Search query filter using Fuse.js fuzzy search
    if (searchQuery.trim()) {
      const fuse = new Fuse(result, {
        keys: [
          'title',
          'payload.username',
          'payload.url',
          'payload.notes',
          'payload.content',
          'payload.cardNumber',
          'payload.cardholderName',
          'payload.type',
          'payload.country',
          'payload.number',
          'payload.licenseKey',
        ],
        threshold: 0.35,
        distance: 100,
      });
      result = fuse.search(searchQuery).map((res) => res.item);
    }

    return result;
  }, [items, currentCategoryType, favoritesOnly, selectedTagId, searchQuery]);

  // Sync active detail item if it's updated in the background
  const activeItemUpdated = useMemo(() => {
    if (!activeDetailItem) return null;
    return items.find((it) => it.id === activeDetailItem.id) || null;
  }, [items, activeDetailItem]);

  useEffect(() => {
    if (activeItemUpdated) {
      setActiveDetailItem(activeItemUpdated);
    }
  }, [activeItemUpdated]);

  // Calculate storage stats (mock for Phase 2, hooked to R2 in Phase 3)
  const storageUsage = useMemo(() => {
    // Count files and photos sizes
    const fileItems = items.filter((it) => it.item_type === 'file' || it.item_type === 'photo');
    const totalBytes = fileItems.reduce((acc, it) => acc + (it.storage_meta?.size || 0), 0);
    const mbs = (totalBytes / (1024 * 1024)).toFixed(1);
    const percentage = Math.min(100, parseFloat(((totalBytes / (100 * 1024 * 1024)) * 100).toFixed(1))); // 100MB limit
    return {
      used: `${mbs} MB`,
      limit: '100 MB',
      percentage,
    };
  }, [items]);

  return (
    <div className="grid gap-8 lg:grid-cols-[240px_1fr]">
      {/* ================= SIDEBAR ================= */}
      <aside className="space-y-6 lg:sticky lg:top-24 h-fit">
        {/* Navigation Filters */}
        <div className="space-y-1.5">
          <span className="text-4xs uppercase tracking-[0.2em] text-ink-faint font-mono font-bold block">
            Filters
          </span>
          <button
            type="button"
            onClick={() => setFavoritesOnly((f) => !f)}
            className={`flex w-full items-center justify-between rounded-md border px-3 py-2 text-xs transition-all ${
              favoritesOnly
                ? 'border-accent bg-accent/5 text-accent shadow-[0_0_15px_rgba(var(--c-accent),0.05)]'
                : 'border-line bg-surface/50 text-ink-muted hover:text-ink hover:border-line-strong'
            }`}
          >
            <span className="flex items-center gap-2 font-medium">
              <Star className={`size-3.5 ${favoritesOnly ? 'fill-current' : ''}`} />
              <span>Favorites Only</span>
            </span>
            <span className="text-3xs font-mono text-ink-faint">
              {items.filter((it) => it.favorite).length}
            </span>
          </button>
        </div>

        {/* Tags filter list */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-4xs uppercase tracking-[0.2em] text-ink-faint font-mono font-bold block">
              Tags
            </span>
            {selectedTagId && (
              <button
                type="button"
                onClick={() => setSelectedTagId(null)}
                className="text-4xs uppercase tracking-wider text-accent hover:text-accent-soft font-semibold"
              >
                Clear
              </button>
            )}
          </div>
          {tags.length > 0 ? (
            <div className="flex flex-wrap gap-1.5 lg:flex-col lg:gap-1">
              {tags.map((tag) => {
                const isSelected = selectedTagId === tag.id;
                const count = items.filter((it) => it.tags?.some((t) => t.id === tag.id)).length;
                return (
                  <button
                    key={tag.id}
                    type="button"
                    onClick={() => setSelectedTagId(isSelected ? null : tag.id)}
                    className={`flex items-center justify-between rounded px-2.5 py-1.5 text-left text-xs transition-colors w-auto lg:w-full ${
                      isSelected
                        ? 'bg-accent text-bg font-semibold'
                        : 'bg-surface/30 border border-line/40 text-ink-muted hover:bg-surface/80 hover:text-ink'
                    }`}
                  >
                    <span className="flex items-center gap-1.5 truncate">
                      <TagIcon className="size-3 flex-shrink-0" />
                      <span className="truncate">{tag.name}</span>
                    </span>
                    <span className={`text-4xs font-mono ${isSelected ? 'text-bg/80' : 'text-ink-faint'}`}>
                      {count}
                    </span>
                  </button>
                );
              })}
            </div>
          ) : (
            <p className="text-2xs text-ink-faint italic leading-relaxed">No tags created yet.</p>
          )}
        </div>

        {/* Storage Indicator */}
        <div className="rounded-md border border-line bg-surface/30 p-3.5 space-y-2">
          <div className="flex items-center justify-between text-4xs uppercase tracking-[0.2em] text-ink-faint font-mono font-bold">
            <span className="flex items-center gap-1.5">
              <HardDrive className="size-3" />
              <span>Storage Usage</span>
            </span>
            <span className="text-ink-muted font-mono">{storageUsage.used}</span>
          </div>
          <div className="h-1.5 w-full rounded-full bg-bg border border-line overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${storageUsage.percentage}%` }}
              transition={{ duration: 0.8, ease: 'easeOut' }}
              className={`h-full rounded-full ${
                storageUsage.percentage > 85 ? 'bg-danger' : 'bg-accent'
              }`}
            />
          </div>
          <div className="flex justify-between text-5xs font-mono text-ink-faint uppercase">
            <span>0%</span>
            <span>Limit: {storageUsage.limit}</span>
          </div>
        </div>
      </aside>

      {/* ================= MAIN PANEL ================= */}
      <main className="space-y-6 min-w-0">
        {/* Controls Bar */}
        <section className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          {/* Search Input */}
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-ink-faint" />
            <input
              ref={searchInputRef}
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search items... (Press '/' to focus)"
              className="w-full rounded-md border border-line bg-surface py-2.5 pl-9 pr-12 text-xs text-ink placeholder:text-ink-faint focus:border-line-strong focus:outline-none transition-colors"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 rounded bg-bg px-1.5 py-0.5 font-mono text-4xs text-ink-faint border border-line hover:text-ink-muted transition-colors"
              >
                ESC
              </button>
            )}
          </div>

          {/* Action Row */}
          <div className="flex items-center justify-between gap-3 sm:justify-end">
            {/* View Mode Toggle */}
            <div className="flex items-center rounded-md border border-line bg-surface/50 p-0.5">
              <button
                type="button"
                onClick={() => setViewMode('grid')}
                className={`rounded p-1.5 transition-colors ${
                  viewMode === 'grid' ? 'bg-bg text-ink shadow' : 'text-ink-faint hover:text-ink-muted'
                }`}
                title="Grid view"
              >
                <Grid className="size-3.5" />
              </button>
              <button
                type="button"
                onClick={() => setViewMode('list')}
                className={`rounded p-1.5 transition-colors ${
                  viewMode === 'list' ? 'bg-bg text-ink shadow' : 'text-ink-faint hover:text-ink-muted'
                }`}
                title="List view"
              >
                <List className="size-3.5" />
              </button>
            </div>

            {/* Add Item Button */}
            <Button
              type="button"
              variant="primary"
              size="md"
              onClick={() => setIsCreateOpen(true)}
              className="flex items-center gap-1.5 shadow-[0_4px_12px_rgba(var(--c-accent),0.15)]"
            >
              <Plus className="size-4" />
              <span>Add Item</span>
            </Button>
          </div>
        </section>

        {/* ================= ITEMS GRID / LIST ================= */}
        {loading ? (
          /* Loading Skeleton Grid */
          <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div
                key={i}
                className="h-[140px] rounded-md border border-line bg-surface/50 animate-pulse p-4 flex flex-col justify-between"
              >
                <div className="flex justify-between">
                  <div className="size-8 rounded bg-bg border border-line" />
                  <div className="w-12 h-3 rounded bg-bg border border-line" />
                </div>
                <div className="space-y-2">
                  <div className="w-2/3 h-4 rounded bg-bg" />
                  <div className="w-1/2 h-3 rounded bg-bg" />
                </div>
                <div className="w-full border-t border-line/40 pt-2 flex gap-1">
                  <div className="w-8 h-3 rounded bg-bg" />
                  <div className="w-10 h-3 rounded bg-bg" />
                </div>
              </div>
            ))}
          </div>
        ) : filteredItems.length > 0 ? (
          /* Items Render Area */
          viewMode === 'grid' ? (
            <motion.div
              layout
              initial="hidden"
              animate="visible"
              variants={staggerContainer}
              className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3"
            >
              {filteredItems.map((item) => (
                <motion.div key={item.id} variants={staggerItem}>
                  <ItemCard item={item} onClick={() => setActiveDetailItem(item)} />
                </motion.div>
              ))}
            </motion.div>
          ) : (
            /* Sleek Horizontal List View */
            <motion.div
              layout
              initial="hidden"
              animate="visible"
              variants={staggerContainer}
              className="divide-y divide-line rounded-md border border-line bg-surface/20 overflow-hidden"
            >
              {filteredItems.map((item) => (
                <motion.div
                  key={item.id}
                  variants={staggerItem}
                  onClick={() => setActiveDetailItem(item)}
                  className="group flex items-center justify-between p-3.5 hover:bg-surface/60 cursor-pointer transition-colors text-xs gap-4"
                >
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <span className="grid size-7 place-items-center rounded border border-line bg-bg text-ink-muted group-hover:text-accent transition-colors flex-shrink-0">
                      {item.item_type === 'password' && <KeyRound className="size-3.5" />}
                      {item.item_type === 'note' && <FileText className="size-3.5" />}
                      {item.item_type === 'card' && <CreditCard className="size-3.5" />}
                      {item.item_type === 'id' && <UserSquare className="size-3.5" />}
                      {item.item_type === 'software_key' && <Binary className="size-3.5" />}
                      {item.item_type === 'file' && <FileText className="size-3.5" />}
                      {item.item_type === 'photo' && <FileText className="size-3.5" />}
                    </span>
                    <div className="min-w-0 space-y-0.5">
                      <span className="font-semibold text-ink group-hover:text-accent transition-colors block truncate">
                        {item.title}
                      </span>
                      <div className="flex items-center gap-2">
                        {item.item_type === 'password' && item.payload?.username && (
                          <span className="font-mono text-3xs text-ink-faint truncate block max-w-[150px]">
                            {item.payload.username}
                          </span>
                        )}
                        {item.item_type === 'card' && item.payload?.cardNumber && (
                          <span className="font-mono text-3xs text-ink-faint block">
                            •••• {item.payload.cardNumber.slice(-4)}
                          </span>
                        )}
                        {item.item_type === 'software_key' && item.payload?.email && (
                          <span className="font-mono text-3xs text-ink-faint truncate block max-w-[150px]">
                            {item.payload.email}
                          </span>
                        )}
                        {item.item_type === 'id' && item.payload?.type && (
                          <span className="text-3xs text-ink-faint truncate block max-w-[150px]">
                            {item.payload.type}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Right segment: tags + favorite status */}
                  <div className="flex items-center gap-4 flex-shrink-0">
                    {/* Tags */}
                    <div className="hidden sm:flex items-center gap-1.5">
                      {item.tags?.slice(0, 3).map((tag) => (
                        <span
                          key={tag.id}
                          className="rounded border border-line bg-bg/50 px-1.5 py-0.5 text-5xs font-mono tracking-tight text-ink-faint uppercase"
                        >
                          {tag.name}
                        </span>
                      ))}
                    </div>

                    {/* Favorite Star */}
                    {item.favorite && <Star className="size-3.5 text-accent fill-current" />}

                    {/* Quick Action arrow */}
                    <CornerDownRight className="size-3.5 text-ink-faint opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                </motion.div>
              ))}
            </motion.div>
          )
        ) : (
          /* ================= EMPTY STATE ================= */
          <motion.section
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4 }}
            className="flex flex-col items-center justify-center rounded-md border border-line border-dashed bg-surface/10 p-12 text-center"
          >
            {/* Custom SVG Illustration based on filter type */}
            <div className="grid size-16 place-items-center rounded-full bg-surface border border-line text-ink-muted">
              {currentCategoryType === 'password' ? (
                <KeyRound className="size-8 text-accent" />
              ) : currentCategoryType === 'note' ? (
                <FileText className="size-8 text-accent" />
              ) : currentCategoryType === 'card' ? (
                <CreditCard className="size-8 text-accent" />
              ) : currentCategoryType === 'id' ? (
                <UserSquare className="size-8 text-accent" />
              ) : currentCategoryType === 'software_key' ? (
                <Binary className="size-8 text-accent" />
              ) : (
                <Compass className="size-8 text-accent" />
              )}
            </div>

            <h3 className="mt-5 text-sm font-semibold text-ink">
              {searchQuery
                ? 'No items match your search'
                : selectedTagId
                  ? 'No items with this tag'
                  : favoritesOnly
                    ? 'No favorites found'
                    : currentCategoryType
                      ? `No ${TYPE_SINGULARS[currentCategoryType].toLowerCase()}s yet`
                      : 'Your vault is empty'}
            </h3>
            <p className="mt-2 max-w-[280px] text-2xs text-ink-muted leading-relaxed">
              {searchQuery
                ? 'Try checking spelling or broadening your search terms to find what you need.'
                : selectedTagId
                  ? 'There are no secrets associated with this tag in this view.'
                  : favoritesOnly
                    ? 'Mark items as favorites to have them show up in this view for quick access.'
                    : currentCategoryType
                      ? `Get started by storing your first encrypted ${TYPE_SINGULARS[currentCategoryType].toLowerCase()}.`
                      : 'Store passwords, documents, license keys, cards, and secure notes safe in one place.'}
            </p>

            {!searchQuery && !selectedTagId && !favoritesOnly && (
              <Button
                type="button"
                variant="subtle"
                size="sm"
                onClick={() => setIsCreateOpen(true)}
                className="mt-6 text-xs px-4 py-2"
              >
                <Plus className="size-3.5 mr-1.5 inline" />
                Add {currentCategoryType ? TYPE_SINGULARS[currentCategoryType] : 'Item'}
              </Button>
            )}
          </motion.section>
        )}
      </main>

      {/* Detail Drawer (View / Edit) */}
      <ItemDetailDrawer
        item={activeDetailItem}
        onClose={() => setActiveDetailItem(null)}
      />

      {/* Create Modal */}
      <ItemCreateModal
        isOpen={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
      />
    </div>
  );
}